import { useEffect, useMemo, useState } from 'react';
import { Paper, Skeleton, Typography } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import { UserPermission } from 'api';
import { ADMIN } from 'common/constants/user';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { MessageBox } from 'components';
import { useGetBaseSetOfQuestionsAndDocuments } from 'services/hooks/firm';
import FirmRequestsFooter from './components/FirmRequestFooter/FirmRequestsFooter';
import FirmRequestsHeader from './components/FirmRequestsHeader/FirmRequestsHeader';
import RequestsContent from './components/RequestContent/RequestContent';
import { FirmSetOfQuestionsAndDocuments } from './types';

const FirmRequests = () => {
  const [updateTaskList, setUpdateTaskList] = useState(false);
  const [{ firmInfo, user, firmList }] = useStore() as unknown as UseStoreValues;
  const { name, id: firmId } = firmInfo || {};
  const [firmSetOfQuestionsAndDocuments, getFirmSetOfQuestionsAndDocuments] = useGetBaseSetOfQuestionsAndDocuments();
  const {
    requested_documents: documents = [],
    questions = [],
    companies = [],
  } = (firmSetOfQuestionsAndDocuments ?? {}) as FirmSetOfQuestionsAndDocuments;

  const firmRequestsProps = {
    documents,
    questions,
    companies,
    setUpdateTaskList,
    firmId,
  };

  const isFirmAdmin = useMemo(() => {
    if (!firmList || !firmInfo) {
      return false;
    }

    const currentFirm = firmList.find(firm => firm.id === firmInfo?.id);
    if (!currentFirm || !Array.isArray(currentFirm.user_permissions)) {
      return false;
    }

    return (
      currentFirm?.user_permissions?.some(
        (permission: UserPermission) => permission.user_id === user?.id && permission.access_type === ADMIN
      ) ?? false
    );
  }, [firmInfo, firmList, user]);

  useEffect(() => {
    if (firmId && getFirmSetOfQuestionsAndDocuments) {
      getFirmSetOfQuestionsAndDocuments(firmId);
      setUpdateTaskList(false);
    }
  }, [firmId, getFirmSetOfQuestionsAndDocuments, updateTaskList]);

  if (!isFirmAdmin && !user?.is_superuser) {
    return (
      <MessageBox
        title="You do not have permissions to view this page"
        tagline="Please contact your supervisor if you believe this is an error"
        fullWidth={false}
      />
    );
  }

  return (
    <Paper
      elevation={2}
      style={{
        padding: '2rem 1rem',
        margin: '1rem',
        overflow: 'auto',
      }}>
      {name && <FirmRequestsHeader name={name} />}
      {!isUndefined(companies) && isEmpty(companies) && (
        <Typography variant="h6" style={{ textAlign: 'center' }}>
          No companies created yet
        </Typography>
      )}
      {!isUndefined(companies) ? (
        <>
          <RequestsContent {...firmRequestsProps} />
          <FirmRequestsFooter {...firmRequestsProps} />
        </>
      ) : (
        <Skeleton variant="rectangular" width="100%" height={400} />
      )}
    </Paper>
  );
};

export default FirmRequests;
